import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon } from 'lucide-react';
import { RecoveryButton } from './RecoveryButton';
interface HeroProps {
  onEstimate: () => void;
}
const brands = [{
  name: 'Paul Stuart',
  logo: 'https://cdn.magicpatterns.com/uploads/gTAv6RYngtksxJQpLWREfj/Paul_Stuart.png',
  size: 'h-5'
}, {
  name: 'Eddy',
  logo: 'https://cdn.magicpatterns.com/uploads/cY9Wc9Lr7DzXK726StRe1c/Eddy.png',
  size: 'h-5'
}, {
  name: 'Finisterre',
  logo: 'https://cdn.magicpatterns.com/uploads/s63oXFtYYtB5zX6Rc7tJ9Y/Finisterre.png',
  size: 'h-5'
}, {
  name: 'Konges Sløjd',
  logo: 'https://cdn.magicpatterns.com/uploads/cRHNfLyvvicqap9kZEM2aB/Konges.png',
  size: 'h-7'
}, {
  name: 'Mini Rodini',
  logo: 'https://cdn.magicpatterns.com/uploads/wYbbqPcEv6uVQrhDpHRCQm/Mini_Rodini.png',
  size: 'h-5'
}, {
  name: 'Nisolo',
  logo: 'https://cdn.magicpatterns.com/uploads/g3A8xqzkMJpxTuLHTL5rtm/Nisolo.png',
  size: 'h-3.5'
}];
export function Hero({
  onEstimate
}: HeroProps) {
  return <section id="top" className="relative overflow-hidden pt-28 sm:pt-32" data-id="element-910" data-mp="3o7c05xxxspgm">
      <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:pb-24" data-id="element-911" data-mp="3o59efq3thihp">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} data-id="element-912" data-mp="3e960zo3ilxfg">
          <span className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-forest/5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-forest" data-id="element-913" data-mp="30fybq32wpe0x">
            <span className="h-1.5 w-1.5 rounded-full bg-forest" data-id="element-914" data-mp="2qlelycqlb6ah" />
            AI-Powered Inventory Recovery
          </span>

          <h1 className="mt-5 font-display text-4xl font-medium leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl" data-id="element-915" data-mp="2vglk7513bis0">
            Your excess and returned inventory is worth more than you think.
          </h1>

          <p className="mt-5 max-w-lg text-lg leading-relaxed text-ink/70" data-id="element-916" data-mp="s1rtgrkfwcvv">
            ManyCo.ai uses pricing, demand, and sell-through intelligence to determine the
            highest-value recovery path—without fire-sale pricing or loss of brand control.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-917" data-mp="i9vpizvo9sjq">
            <RecoveryButton onClick={onEstimate} className="rounded-full bg-forest px-7 py-4 text-base font-semibold text-cream shadow-sm transition-colors hover:bg-forest-dark" data-id="element-918" data-mp="deyjm1tovzb2" />
            <p className="text-sm text-ink/55" data-id="element-919" data-mp="15zzr71pavi83">
              Find out how much your excess &amp; returned inventory is worth
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        ease: 'easeOut',
        delay: 0.1
      }} className="relative" data-id="element-920" data-mp="x0uyic0f6j3u">
          <div className="overflow-hidden rounded-3xl border border-ink/10 bg-paper shadow-xl" data-id="element-921" data-mp="1x9b4uzsqezc1">
            <img src="https://cdn.magicpatterns.com/uploads/ark1vLwmbdepw2CtNaaVSU/Eddy_White.png" alt="Woman in a white embroidered dress holding lilies in a sunlit field" className="h-full w-full object-cover" data-id="element-922" data-mp="21awbi3eoahds" />
          </div>
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="absolute -bottom-5 -left-4 rounded-2xl border border-ink/10 bg-paper px-5 py-4 shadow-lg sm:-left-6" data-id="element-923" data-mp="2b2soeklf4c22">
            <p className="text-xs font-medium text-ink/50" data-id="element-924" data-mp="2p1a5w0kqcsbj">Higher recovery vs. liquidation</p>
            <p className="font-display text-2xl font-semibold text-forest" data-id="element-925" data-mp="2tz1zcz10wmhu">2–15x</p>
          </motion.div>
        </motion.div>
      </div>

      <div className="mx-auto max-w-6xl px-5 pb-16 sm:px-8 lg:pb-20" data-id="element-926" data-mp="zlkl62mid1zh">
        <div className="flex items-center gap-2 text-xs font-medium text-ink/50" data-id="element-927" data-mp="zlstdh7orj4c">
          <ShieldCheckIcon className="h-4 w-4 text-forest" data-id="element-928" data-mp="49t29q7vsqqr" />
          Trusted by 180+ retail &amp; DTC brands
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-5 opacity-35 sm:gap-x-10 lg:gap-x-12" data-id="element-929" data-mp="3vjrs687e5pqh">
          {brands.map(brand => <div key={brand.name} className="flex h-7 items-center" data-id="element-930" data-mp="9a7dti8oyitj">
              <img src={brand.logo} alt={brand.name} className={`${brand.size} w-auto max-w-[150px] object-contain`} data-id="element-931" data-mp="3hqaayu26gp3m" />
            </div>)}
        </div>
      </div>
    </section>;
}